import "../../../components/common/Tables/DataTable.css"
import React, { useState } from "react";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { Tooltip } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DataTableFoot, SortButton } from "../../../components/common/Tables/DataTableParts";
import { EyeIcon, PencilIcon, TrashIcon } from "../../../components/common/Icons/Icons";

type Order = 'asc' | 'desc';
const headers = [
  {id:'status', label:'Status', align:"center"},
  {id:'title', label:'Title', align:null},
  {id:'body', label:'Body', align:null},
  {id:'start_date', label:'Start', align:null},
  {id:'end_date', label:'End', align:null},
  {id:'created_at', label:'Created', align:null},
  {id:'author', label:'Author', align:null},
  {id:'preview', label:'Preview', align:"center"},
  {id:'edit', label:'Edit', align:"center"},
  {id:'delete', label:'Delete', align:"center"},
];

const unsortable = ['status', 'edit', 'delete'];

// Row actions pinned to the right edge (px, fits each heading)
const actionWidths: Record<string, number> = {
  preview: 76,
  edit: 60,
  delete: 68,
};

interface TableProps {
  setPromoToModify: any;
  setEditOpen: any;
  setDeleteOpen: any;
  setViewOpen: any;
  setPreviewOpen: any;
  // Page controls (Create Promo, search) shown in the table's toolbar
  toolbar?: React.ReactNode;
}

const PromosTable: React.FC<TableProps> = ({
  setPromoToModify,
  setEditOpen,
  setDeleteOpen,
  setViewOpen,
  setPreviewOpen,
  toolbar,
}) => {
  const { data: promos, loading, meta } = useSelector((state: RootState) => state.promos);
  const [searchParams, setSearchParams] = useSearchParams();
  // Table Sort (read from the URL so the arrow survives a reload)
  const [order, setOrder] = useState<Order>(searchParams.get('sort_direction') === 'desc' ? 'desc' : 'asc');
  const [orderBy, setOrderBy] = useState(searchParams.get('sort_by') ?? '');
  const navigate = useNavigate();

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const total = meta?.total ?? 0;

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(header_id);
    searchParams.set("sort_direction", newOrder);
    searchParams.set("sort_by", header_id);
    setSearchParams(searchParams);
  };

  // Keeps the search and sort (the old pager replaced the whole query with ?page=)
  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  };

  const isExpired = (end_date: string | null) => {
    if (!end_date) return false;
    return new Date(end_date) < new Date();
  };

  const isUpcoming = (start_date: string | null) => {
    if (!start_date) return false;
    return new Date(start_date) > new Date();
  };

  const statusPill = (promo: any) => {
    if (promo?.is_draft) return <span className="dt-pill accent">Draft</span>;
    if (isExpired(promo.end_date)) return <span className="dt-pill neutral">Expired</span>;
    if (isUpcoming(promo.start_date)) return <span className="dt-pill info">Upcoming</span>;
    return <span className="dt-pill good">Active</span>;
  };

  const open = (promo: any, setModal: (open: boolean) => void) => (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setPromoToModify(promo);
    setModal(true);
  };

  const pinned = headers.map((header) => header.id).filter((id) => id in actionWidths);

  const pinStyle = (id: string) => {
    const index = pinned.indexOf(id);
    if (index < 0) return undefined;
    const right = pinned.slice(index + 1).reduce((sum, next) => sum + actionWidths[next], 0);
    return {'--dt-right': `${right}px`, '--dt-w': `${actionWidths[id]}px`} as React.CSSProperties;
  };

  const cellClass = (header: typeof headers[number]) => [
    header.align ? 'dt-center' : '',
    pinned.includes(header.id) ? 'dt-pin' : '',
    pinned[0] === header.id ? 'dt-pin-start' : '',
  ].filter(Boolean).join(' ') || undefined;

  const authorName = (promo: any) => promo?.author?.name ?? promo?.author?.username ?? promo?.author?.email;

  const renderCell = (id: string, promo: any) => {
    switch (id) {
      case 'status':
        return statusPill(promo);

      case 'title':
        // A real button so the details open from the keyboard too
        return <button type="button" className="dt-title-btn" onClick={open(promo, setViewOpen)}>{promo?.title}</button>;

      case 'body':
        return <span className="dt-body" title={promo?.body}>{promo?.body}</span>;

      case 'start_date':
        return promo?.start_date && <span className="dt-date">{new Date(promo.start_date).toLocaleDateString()}</span>;

      case 'end_date':
        return promo?.end_date && <span className="dt-date">{new Date(promo.end_date).toLocaleDateString()}</span>;

      case 'created_at':
        return promo?.created_at && <span className="dt-date">{new Date(promo.created_at).toLocaleString()}</span>;

      case 'author':
        return promo?.author?.id ? (
          <a
            href={`/user/${promo.author.id}`}
            className="dt-link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              if (e.metaKey || e.ctrlKey || e.shiftKey) return;
              e.preventDefault();
              navigate(`/user/${promo.author.id}`);
            }}
          >
            {authorName(promo)}
          </a>
        ) : authorName(promo);

      case 'preview':
        return (
          <Tooltip title="Preview promo" arrow>
            <button type="button" className="dt-act" onClick={open(promo, setPreviewOpen)} aria-label="Preview promo"><EyeIcon/></button>
          </Tooltip>
        );

      case 'edit':
        return (
          <Tooltip title="Edit promo" arrow>
            <button type="button" className="dt-act" onClick={open(promo, setEditOpen)} aria-label="Edit promo"><PencilIcon/></button>
          </Tooltip>
        );

      case 'delete':
        return (
          <Tooltip title="Delete promo" arrow>
            <button type="button" className="dt-act danger" onClick={open(promo, setDeleteOpen)} aria-label="Delete promo"><TrashIcon/></button>
          </Tooltip>
        );

      default:
        return null;
    }
  };

  return (
    <div className="dt">
      {toolbar && <div className="dt-toolbar">
        <div className="dt-toolbar-start">{toolbar}</div>
      </div>}

      <div className="dt-scroll">
        {loading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading promos"/></div>
        ) : promos.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={cellClass(header)}
                    style={pinStyle(header.id)}
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {!unsortable.includes(header.id) ? (
                      <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={() => handleRequestSort(header.id)}/>
                    ) : header.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {promos.map((promo: any) => (
                <tr key={promo.id} className="dt-clickable" onClick={open(promo, setViewOpen)}>
                  {headers.map((header) => (
                    <td key={header.id} className={cellClass(header)} style={pinStyle(header.id)}>
                      {renderCell(header.id, promo)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state"><div className="dt-empty-title">No Promos Found</div></div>
        )}
      </div>

      <DataTableFoot
        shown={promos?.length ?? 0}
        total={total}
        noun={total === 1 ? 'Promo' : 'Promos'}
        page={page}
        totalPages={meta?.total_pages ?? 1}
        loading={loading}
        onPage={goToPage}
      />
    </div>
  );
};

export default PromosTable;
